// src/components/atomic-crm/deals/DealPipelineCards.tsx
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  useCreatePath,
  useDataProvider,
  useListContext,
  useNotify,
} from "ra-core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ds/ui/card";
import { Badge } from "@/components/ds/ui/badge";
import { Button } from "@/components/ds/ui/button";
import { ChevronDown, ChevronRight, MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ds/ui/dropdown-menu";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { findDealLabel } from "./deal";
import type { Deal } from "../types";

export const DealPipelineCards = () => {
  const { data: deals, isPending } = useListContext<Deal>();
  const { dealStages } = useConfigurationContext();

  if (isPending) return null;

  const grouped = dealStages.map((stage) => {
    const stageDeals = (deals ?? []).filter((d) => d.stage === stage.value);
    const total = stageDeals.reduce(
      (sum, d) => sum + (Number(d.amount) || 0),
      0,
    );
    return {
      stage,
      deals: stageDeals,
      total,
      label: findDealLabel(dealStages, stage.value) ?? stage.value,
    };
  });

  return (
    <div className="space-y-4">
      {grouped.map((group) => (
        <StageSection key={group.stage.value} {...group} />
      ))}
    </div>
  );
};

const StageSection = ({
  stage,
  label,
  deals,
  total,
}: {
  stage: any;
  label: string;
  deals: Deal[];
  total: number;
}) => {
  const [open, setOpen] = useState(deals.length > 0);

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-muted/40 hover:bg-muted/60 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          {open ? (
            <ChevronDown className="h-4 w-4 shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0" />
          )}
          <span className="font-medium truncate">{label}</span>
          <Badge variant="secondary" className="shrink-0">
            {deals.length}
          </Badge>
        </div>
        <span className="text-sm text-muted-foreground shrink-0">
          ${total.toLocaleString()}
        </span>
      </button>

      {open && (
        <div className="p-3 space-y-2">
          {deals.length === 0 ? (
            <p className="text-sm text-muted-foreground px-1 py-2">
              No deals in this stage.
            </p>
          ) : (
            deals.map((deal) => <DealCard key={deal.id} deal={deal} />)
          )}
        </div>
      )}
    </div>
  );
};

const DealCard = ({ deal }: { deal: Deal }) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const createPath = useCreatePath();
  const { dealStages } = useConfigurationContext();
  const queryClient = useQueryClient();

  const { mutate: updateStage } = useMutation({
    mutationFn: async ({ id, stage }: { id: number; stage: string }) => {
      return dataProvider.update("deals", {
        id,
        data: { stage },
        previousData: deal,
      });
    },
    onSuccess: () => {
      notify("Deal moved", { type: "success" });
      queryClient.invalidateQueries({ queryKey: ["deals"] });
    },
    onError: () => {
      notify("Failed to move deal", { type: "error" });
    },
  });

  return (
    <Card className="hover:bg-muted/40 transition-colors">
      <CardContent className="p-3 flex items-start justify-between gap-3">
        <Link
          to={createPath({ resource: "deals", id: deal.id, type: "show" })}
          className="flex-1 min-w-0 no-underline"
        >
          {/* ✅ THE ACTUAL DEAL CONTENT */}
          <div className="font-medium truncate">
            {deal.name || "(untitled deal)"}
          </div>
          <div className="text-xs text-muted-foreground mt-1 truncate">
            {typeof deal.amount === "number"
              ? `$${deal.amount.toLocaleString()}`
              : "—"}
            {deal.category ? ` · ${deal.category}` : ""}
          </div>
        </Link>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {dealStages
              .filter((s) => s.value !== deal.stage)
              .map((s) => (
                <DropdownMenuItem
                  key={s.value}
                  onClick={() => updateStage({ id: deal.id, stage: s.value })}
                >
                  Move to {s.label}
                </DropdownMenuItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </CardContent>
    </Card>
  );
};
